// Iter 575 — Shopee Langsung Gudang (Polosan / Biaya Tambahan) untuk Rido. Tanpa Mockup.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { RefreshCw, PackageCheck, Receipt, AlertTriangle, CheckCircle2, Search, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import "./shopee_direct_warehouse.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/shopee/direct`;
const CATS = [
  { key: "gudang_polosan", label: "Polosan", icon: PackageCheck },
  { key: "gudang_layanan", label: "Biaya Tambahan / Layanan", short: "Biaya Tambahan", icon: Receipt },
];
const TABS = [
  { key: "belum_diterima", label: "Belum Diterima" },
  { key: "menunggu_barang", label: "Menunggu Barang / Dokumen", short: "Menunggu Barang" },
  { key: "siap_diproses", label: "Siap Diproses" },
  { key: "selesai", label: "Selesai" },
];
const ROUTE_LABEL = { mockup: "Mockup", gudang_polosan: "Gudang · Polosan", gudang_layanan: "Gudang · Layanan" };
// Label panjang di desktop, versi pendek di HP (CSS .sdw-lbl-*).
const Lbl = ({ full, short }) => short
  ? <><span className="sdw-lbl-full">{full}</span><span className="sdw-lbl-short" aria-hidden="true">{short}</span></> : full;
const errMsg = (e, d) => { const x = e?.response?.data?.detail; return typeof x === "string" ? x : (x?.detail || d); };

function fmt(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d) || d.getFullYear() < 2015) return "-";
  return d.toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta" });
}

const SHOPEE_STATUS = { to_ship: "Perlu Dikirim", ready_to_ship: "Siap Kirim", processed: "Diproses",
  shipped: "Dikirim", completed: "Selesai", cancelled: "Dibatalkan", canceled: "Dibatalkan",
  cancelling: "Proses Batal", in_cancel: "Proses Batal", pending_payment: "Belum Bayar" };

function Items({ items }) {
  return (
    <ul className="sdw-items">
      {(items || []).map((it, i) => (
        <li key={i}><span className="sdw-item-name">{it.name}</span>
          {it.variation && <span className="sdw-item-var">{it.variation}</span>}
          <b>× {it.quantity || 0}</b></li>
      ))}
    </ul>
  );
}

// Baris ringkas: nomor · pembeli · jenis · pcs · status Shopee · batas kirim · aksi.
function Row({ number, buyer, service, pcs, status, deadline, urgency, open, onToggle, children }) {
  return (
    <div className="sdw-row">
      <button className="sdw-row-main" onClick={onToggle} aria-expanded={open} data-testid={`sdw-toggle-${number}`}>
        {open ? <ChevronDown size={15}/> : <ChevronRight size={15}/>}
        <strong className="sdw-no" data-testid={`sdw-order-${number}`}>{number}</strong>
        <span className="sdw-buyer" data-testid={`sdw-buyer-${number}`}>{buyer || "-"}</span>
      </button>
      <span className={service ? "sdw-type sdw-type-service" : "sdw-type"} data-testid={`sdw-type-${number}`}>
        {service ? "Biaya Tambahan" : "Polosan"}</span>
      <span className="sdw-pcs" data-testid={`sdw-pcs-${number}`}>{service ? "—" : `${pcs || 0} pcs`}</span>
      <span className="sdw-status" data-testid={`sdw-shopee-status-${number}`}>{SHOPEE_STATUS[status] || status || "-"}</span>
      <span className={`sdw-urg sdw-urg-${urgency || "normal"}`} data-testid={`sdw-deadline-${number}`}>{fmt(deadline)}</span>
      <div className="sdw-row-actions">{children}</div>
    </div>
  );
}

function UnacceptedCard({ o, busy, onAccept }) {
  const [open, setOpen] = useState(false);
  const n = o.external_order_id, service = o.route === "gudang_layanan";
  return (
    <article className="sdw-card" data-testid={`sdw-unaccepted-${n}`}>
      <Row number={n} buyer={o.buyer_username || o.customer_name} service={service} pcs={o.pcs} status={o.status}
        deadline={o.ship_by_date_iso} urgency={o.urgency} open={open} onToggle={() => setOpen(!open)}>
        <button className="sdw-btn sdw-btn-primary" disabled={busy} onClick={() => onAccept(o)}
          data-testid={`sdw-accept-${n}`}>{busy ? "Memverifikasi…" : "Terima ke Gudang"}</button>
      </Row>
      {open && <div className="sdw-detail" data-testid={`sdw-detail-${n}`}>
        {service && <div className="sdw-badge-service">LAYANAN — bukan pakaian. Periksa pesanan utama & dokumen.</div>}
        <Items items={o.items}/>
        <div className="sdw-meta">Jalur: {o.route_reason}{o.route_manual ? " (ditetapkan manual)" : ""}
          {(o.fee_labels || []).length ? ` · ${o.fee_labels.join(", ")}` : ""}</div>
      </div>}
    </article>
  );
}

function FinishForm({ job, onSubmit, onCancel }) {
  const [checked, setChecked] = useState(false);
  const [ref, setRef] = useState(job.main_order_ref || "");
  const n = job.order_number;
  return (
    <div className="sdw-finish" data-testid={`sdw-finish-form-${n}`}>
      <label className="sdw-check"><input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)}
        data-testid={`sdw-finish-check-${n}`}/>
        {job.is_service ? "Dokumen & keterkaitan pesanan utama sudah saya periksa" : "Barang sudah saya periksa"}</label>
      {job.is_service && <input className="sdw-input" value={ref} onChange={e => setRef(e.target.value)}
        placeholder="Nomor pesanan utama, atau keterangan jelas bila belum diketahui (wajib)" data-testid={`sdw-finish-ref-${n}`}/>}
      <p className="sdw-meta">Selesai di Gudang tidak mengubah status pengiriman Shopee. Resi tetap dicetak lewat Shopee.</p>
      <div className="sdw-actions">
        <button className="sdw-btn" onClick={onCancel} data-testid={`sdw-finish-cancel-${n}`}>Batal</button>
        <button className="sdw-btn sdw-btn-primary" disabled={!checked || (job.is_service && ref.trim().length < 5)}
          onClick={() => onSubmit({ state: "selesai", checked, main_order_ref: ref })}
          data-testid={`sdw-finish-submit-${n}`}>Tandai Selesai</button>
      </div>
    </div>
  );
}

function JobActions({ job, busy, onState, onFinish }) {
  const n = job.order_number;
  if (job.state === "menunggu_barang") return <button className="sdw-btn sdw-btn-primary" disabled={busy}
    onClick={() => onState(job, { state: "siap_diproses" })} data-testid={`sdw-ready-${n}`}>
    {job.is_service ? "Dokumen Siap" : "Barang Siap"}</button>;
  if (job.state === "siap_diproses") return <>
    <button className="sdw-btn" disabled={busy} onClick={() => onState(job, { state: "menunggu_barang" })}
      data-testid={`sdw-back-${n}`}>Kembali</button>
    <button className="sdw-btn sdw-btn-primary" disabled={busy} onClick={onFinish} data-testid={`sdw-finish-${n}`}>Selesai…</button>
  </>;
  return <span className="sdw-done"><CheckCircle2 size={14}/> Selesai</span>;
}

function JobCard({ job, busy, onState }) {
  const [open, setOpen] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const n = job.order_number;
  return (
    <article className={`sdw-card ${job.shopee_cancelled ? "sdw-card-alert" : ""}`} data-testid={`sdw-job-${n}`}>
      <Row number={n} buyer={job.buyer_username || job.customer_name} service={job.is_service} pcs={job.pcs}
        status={job.shopee_status} deadline={job.ship_by_date_iso} urgency="normal" open={open || finishing}
        onToggle={() => setOpen(!open)}>
        <JobActions job={job} busy={busy} onState={onState} onFinish={() => setFinishing(true)}/>
      </Row>
      {job.shopee_cancelled && <div className="sdw-warn" data-testid={`sdw-cancel-warn-${n}`}>
        <AlertTriangle size={14}/> Shopee: pesanan dibatalkan. Periksa sebelum memproses.</div>}
      {(open || finishing) && <div className="sdw-detail" data-testid={`sdw-detail-${n}`}>
        {job.is_service && <div className="sdw-badge-service">LAYANAN — bukan pakaian. Periksa pesanan utama & dokumen.</div>}
        <Items items={job.items}/>
        <div className="sdw-meta">Diterima {job.accepted_by_name} · {fmt(job.accepted_at)}
          {job.main_order_ref ? ` · Pesanan utama: ${job.main_order_ref}` : ""}
          {job.state === "selesai" ? ` · Selesai oleh ${job.completed_by_name} ${fmt(job.completed_at)}` : ""}</div>
        {finishing && <FinishForm job={job} onCancel={() => setFinishing(false)}
          onSubmit={async (p) => { if (await onState(job, p)) setFinishing(false); }}/>}
      </div>}
    </article>
  );
}

function RouteCard({ o, busy, onRoute }) {
  const [reason, setReason] = useState("");
  const n = o.external_order_id;
  return (
    <article className="sdw-card" data-testid={`sdw-perlu-${n}`}>
      <div className="sdw-detail">
        <div className="sdw-meta"><strong className="sdw-no">{n}</strong> · {o.buyer_username || o.customer_name || "-"}
          · {SHOPEE_STATUS[o.status] || o.status} · kirim {fmt(o.ship_by_date_iso)}</div>
        <Items items={o.items}/>
        <div className="sdw-meta">Alasan otomatis: {o.route_reason}</div>
        <input className="sdw-input" value={reason} onChange={e => setReason(e.target.value)}
          placeholder="Alasan keputusan (wajib)" data-testid={`sdw-route-reason-${n}`}/>
        <div className="sdw-actions">
          {Object.entries(ROUTE_LABEL).map(([k, lbl]) => (
            <button key={k} className="sdw-btn" disabled={busy || reason.trim().length < 3}
              onClick={() => onRoute(o, k, reason)} data-testid={`sdw-route-${k}-${n}`}>{lbl}</button>
          ))}
        </div>
      </div>
    </article>
  );
}

export function ShopeeDirectWarehouseSection() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [cat, setCat] = useState("gudang_polosan");
  const [tab, setTab] = useState("belum_diterima");
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/queue`, { withCredentials: true })).data); setErr(""); }
    catch (e) { setErr(errMsg(e, "Gagal memuat antrean Pesanan Tanpa Mockup")); }
  }, []);
  useEffect(() => { load(); const t = setInterval(() => { if (document.visibilityState === "visible") load(); }, 60000); return () => clearInterval(t); }, [load]);

  const run = async (id, fn, ok) => {
    setBusyId(id);
    try { await fn(); toast.success(ok); await load(); return true; }
    catch (e) { toast.error(errMsg(e, "Gagal")); await load(); return false; }
    finally { setBusyId(null); }
  };
  const accept = (o) => run(o.id, () => axios.post(`${API}/accept/${o.id}`, {}, { withCredentials: true }), `Pesanan ${o.external_order_id} diterima Gudang`);
  const setState = (j, p) => run(j.id, () => axios.post(`${API}/jobs/${j.id}/state`, p, { withCredentials: true }), "Status diperbarui");
  const route = (o, r, reason) => run(o.id, () => axios.post(`${API}/route/${o.id}`, { route: r, reason }, { withCredentials: true }), `Jalur ${ROUTE_LABEL[r]} ditetapkan`);

  const match = useCallback((o) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [o.external_order_id, o.order_number, o.buyer_username, o.customer_name, ...(o.items || []).map(i => i.name)]
      .some(h => String(h || "").toLowerCase().includes(s));
  }, [q]);
  const list = useMemo(() => {
    if (!data) return [];
    if (tab === "perlu_dicek") return data.perlu_dicek.filter(match);
    if (tab === "belum_diterima") return data.unaccepted.filter(o => o.route === cat).filter(match);
    return data.jobs.filter(j => j.route === cat && j.state === tab).filter(match);
  }, [data, tab, cat, match]);
  const countFor = (t, c) => !data ? 0 : t === "belum_diterima"
    ? data.unaccepted.filter(o => o.route === c).length : data.jobs.filter(j => j.route === c && j.state === t).length;

  return (
    <section className="sdw" data-testid="shopee-direct-warehouse">
      {err && <div className="sdw-warn" data-testid="sdw-error">{err}</div>}
      <nav className="sdw-cats" role="tablist">
        {CATS.map(c => { const I = c.icon; return (
          <button key={c.key} className={`sdw-cat ${cat === c.key && tab !== "perlu_dicek" ? "active" : ""}`}
            onClick={() => { setCat(c.key); if (tab === "perlu_dicek") setTab("belum_diterima"); }} data-testid={`sdw-cat-${c.key}`}>
            <I size={16}/> <Lbl full={c.label} short={c.short}/></button>); })}
        {data?.can_decide_route && <button className={`sdw-cat ${tab === "perlu_dicek" ? "active" : ""}`}
          onClick={() => setTab("perlu_dicek")} data-testid="sdw-tab-perlu-dicek">
          <AlertTriangle size={16}/> Perlu Dicek ({data.counts.perlu_dicek})</button>}
      </nav>
      {tab !== "perlu_dicek" && <nav className="sdw-tabs" role="tablist">
        {TABS.map(t => <button key={t.key} className={`sdw-tab ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)}
          data-testid={`sdw-tab-${t.key}`}><Lbl full={t.label} short={t.short}/> <span className="sdw-count">{countFor(t.key, cat)}</span></button>)}
      </nav>}
      <div className="sdw-toolbar">
        <label className="sdw-search"><Search size={14}/><input type="search" value={q} onChange={e => setQ(e.target.value)}
          onKeyDown={e => { if (e.key === "Escape") setQ(""); }}
          placeholder="Cari nomor pesanan / pelanggan / produk" data-testid="sdw-search"/></label>
        <button className="sdw-btn" onClick={load} aria-label="Muat Ulang" data-testid="sdw-refresh"><RefreshCw size={14}/> <span className="sdw-btn-text">Muat Ulang</span></button>
      </div>
      {!data ? <p className="sdw-empty">Memuat…</p> : list.length === 0
        ? <p className="sdw-empty" data-testid="sdw-empty"><CheckCircle2 size={16}/> Tidak ada pesanan.</p>
        : <div className="sdw-list">{list.map(o => tab === "perlu_dicek"
          ? <RouteCard key={o.id} o={o} busy={busyId === o.id} onRoute={route}/>
          : tab === "belum_diterima" ? <UnacceptedCard key={o.id} o={o} busy={busyId === o.id} onAccept={accept}/>
            : <JobCard key={o.id} job={o} busy={busyId === o.id} onState={setState}/>)}</div>}
    </section>
  );
}
